import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, where, collectionGroup, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EyeIcon, CheckCircleIcon, XCircleIcon } from '../../components/icons/Icons';
import ApprovalDetailModal from './components/ApprovalDetailModal';

// This component handles the entire approval workflow for managers and admins.
export default function ApprovalCenter({ userProfile, showModal, appId }) {
    const [approvalItems, setApprovalItems] = useState([]);
    const [pastItems, setPastItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [pastSearchTerm, setPastSearchTerm] = useState('');
    const [approversMap, setApproversMap] = useState(new Map());

    // --- STEP 1: Add state for pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        const usersQuery = query(collection(db, `/artifacts/${appId}/users`), where("role", "in", ["Admin", "Approver"]));
        const unsub = onSnapshot(usersQuery, (snapshot) => {
            const newMap = new Map();
            snapshot.forEach(doc => {
                newMap.set(doc.id, doc.data().name);
            });
            setApproversMap(newMap);
        });
        return () => unsub();
    }, [appId]);
    

    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        let pendingPRsQuery, pendingInvoicesQuery, pastPrQuery, pastInvoicesQuery;
        /*
        if (userProfile.role === 'Admin') {
            pendingPRsQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`), where("status", "==", "Pending Manager Approval"));
            pendingInvoicesQuery = query(collectionGroup(db, 'invoices'), where("status", "==", "Pending Invoice Approval"));
            pastPrQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`), where("status", "in", ["Approved", "Rejected", "Rejected by Finance", "Paid", "Purchase Approved; Vendor Pending Onboarding"]));
            pastInvoicesQuery = query(collectionGroup(db, 'invoices'), where("status", "in", ["Approved for Payment", "Rejected", "Rejected by Finance", "Paid"]));
        } else {
        */ 
            pendingPRsQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`), where("status", "==", "Pending Manager Approval"), where("assignedApproverId", "==", userProfile.uid));
            pendingInvoicesQuery = query(collectionGroup(db, 'invoices'), where("status", "==", "Pending Invoice Approval"), where("assignedApproverId", "==", userProfile.uid));
            pastPrQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`), where("approvedById", "==", userProfile.uid));
            pastInvoicesQuery = query(collectionGroup(db, 'invoices'), where("approvedById", "==", userProfile.uid));
        //}
        
        const prUnsubscribe = onSnapshot(pendingPRsQuery, (snapshot) => {
            const pendingPRs = snapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Purchase' }));
            setApprovalItems(currentItems => [...currentItems.filter(item => item.type !== 'Purchase'), ...pendingPRs]);
            setIsLoading(false);
        });

        const invoicesUnsubscribe = onSnapshot(pendingInvoicesQuery, (snapshot) => {
            const pendingInvoices = snapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Invoice', purchaseRequestId: d.ref.parent.parent.id }));
            setApprovalItems(currentItems => [...currentItems.filter(item => item.type !== 'Invoice'), ...pendingInvoices]);
            setIsLoading(false);
        });

        const pastPrUnsubscribe = onSnapshot(pastPrQuery, (snapshot) => {
            const pastPRs = snapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Purchase' }));
            setPastItems(current => [...current.filter(i => i.type !== 'Purchase'), ...pastPRs]);
        });

        const pastInvoicesUnsubscribe = onSnapshot(pastInvoicesQuery, (snapshot) => {
            const pastInvoices = snapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Invoice', purchaseRequestId: d.ref.parent.parent.id }));
            setPastItems(current => [...current.filter(i => i.type !== 'Invoice'), ...pastInvoices]);
        });

        return () => {
            prUnsubscribe();
            invoicesUnsubscribe();
            pastPrUnsubscribe();
            pastInvoicesUnsubscribe();
        };
    }, [appId, userProfile]);

    const handleAction = async (item, actionStatus, comments = "") => {
        let docRef;
        let finalStatus = actionStatus;

        if (item.type === 'Purchase') {
            docRef = doc(db, `/artifacts/${appId}/public/data/purchaseRequests`, item.id);
            if (item.vendorType === 'new' && actionStatus === 'Approved') {
                finalStatus = 'Purchase Approved; Vendor Pending Onboarding';
                const vendorsCollectionPath = `/artifacts/${appId}/public/data/vendors`;
                const q = query(collection(db, vendorsCollectionPath), where("purchaseRequestId", "==", item.id));
                
                try {
                    const vendorSnap = await getDocs(q);
                    if (!vendorSnap.empty) {
                        const vendorDoc = vendorSnap.docs[0];
                        await updateDoc(vendorDoc.ref, { pr_approved: true });
                    }
                } catch (e) {
                     console.error("Failed to update linked vendor:", e);
                }
            }
        } else {
            docRef = doc(db, `/artifacts/${appId}/public/data/purchaseRequests/${item.purchaseRequestId}/invoices`, item.id);
            if (actionStatus === 'Approved') {
                finalStatus = 'Approved for Payment';
            }
        }

        const itemSnap = await getDoc(docRef);
        const itemData = itemSnap.data();

        try {
            await updateDoc(docRef, {
                status: finalStatus,
                approvalComments: comments,
                approvedBy: userProfile.name,
                approvedById: userProfile.uid,
                approvalDate: new Date()
            });

            setApprovalItems(prevItems => prevItems.filter(prevItem => prevItem.id !== item.id));
            if(isModalOpen) setIsModalOpen(false);
            if(isRejectModalOpen) setIsRejectModalOpen(false);

              /*
              try {
                const requesterId = itemData.requesterId || itemData.submittedById;
                if (requesterId) {
                    const requesterRef = doc(db, `/artifacts/${appId}/users`, requesterId);
                    const requesterSnap = await getDoc(requesterRef);
                    if (requesterSnap.exists()) {
                        // --- FIX: Added a defensive check for the email address ---
                        const requesterEmail = requesterSnap.data().email;
                        if (requesterEmail) { // Check if the email exists
                            const updatedItemData = { ...itemData, status: finalStatus, approvalComments: comments };
                            const emailContent = statusChangeTemplate(updatedItemData, finalStatus);
                            await sendNotificationEmail({ to: [requesterEmail], ...emailContent, appId });
                        } else {
                            console.error(`Notification not sent: Requester ${requesterId} does not have an email address.`);
                        }
                    }
                }

                if (finalStatus === 'Approved' || finalStatus === 'Approved for Payment') {
                    const financeEmail = await getFinanceEmailByEntity(itemData.billingEntity);
                    if (financeEmail) {
                        const itemType = item.type === 'Purchase' ? 'Purchase Request' : 'Invoice';
                        const emailContent = financeApprovalTemplate(itemData, itemType);
                        await sendNotificationEmail({ to: [financeEmail], ...emailContent, appId });
                    }
                }

                if (finalStatus === 'Purchase Approved; Vendor Pending Onboarding') {
                    const configDoc = await getDoc(doc(db, 'config', 'notifications'));
                    const financeEmailsMap = configDoc.data()?.financeEmails;
                    if (financeEmailsMap) {
                        const allFinanceEmails = Object.values(financeEmailsMap);
                        const vendorData = { companyName: itemData.newVendorName, submittedBy: itemData.requesterName };
                        const emailContent = newVendorTemplate(vendorData);
                        await sendNotificationEmail({ to: allFinanceEmails, ...emailContent, appId });
                    }
                }

            } catch (notificationError) {
                console.error("Failed to send notification:", notificationError);
            }
	    */

        } catch (e) {
            console.error("Error updating status:", e);
            showModal("Error", "Failed to update status. Please try again.");
        }
    };

    const handleQuickReject = (item) => {
        setSelectedItem(item);
        setIsRejectModalOpen(true);
    };

    const sortedItems = useMemo(() => {
        let items = activeTab === 'pending' ? approvalItems : pastItems;

        if (activeTab === 'past' && pastSearchTerm) {
            const lowercasedTerm = pastSearchTerm.toLowerCase();
            items = items.filter(item => {
                const id = item.invoiceNumber || item.requestNumber || '';
                const vendor = item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor) || '';
                const requester = item.requesterName || item.submittedBy || '';
                return id.toLowerCase().includes(lowercasedTerm) ||
                       vendor.toLowerCase().includes(lowercasedTerm) ||
                       requester.toLowerCase().includes(lowercasedTerm);
            });
        }

        const sorted = [...items].sort((a, b) => {
            const getValue = (item, key) => {
                if (key === 'id') return item.invoiceNumber || item.requestNumber;
                if (key === 'vendor') return item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor);
                if (key === 'requester') return item.requesterName || item.submittedBy;
		if (key === 'approver') {
                    const approverId = item.assignedApproverId || item.approvedById;
                    return approversMap.get(approverId) || '';
                }                
		if (key === 'amount') return item.approxValue || item.amount;
                return item[key];
            };

            let aValue = getValue(a, sortConfig.key);
            let bValue = getValue(b, sortConfig.key);
            
            if (aValue?.toDate && bValue?.toDate) {
                aValue = aValue.toDate();
                bValue = bValue.toDate();
            } else if (aValue?.toDate) {
                return -1;
            } else if (bValue?.toDate) {
                return 1;
            }

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [activeTab, approvalItems, pastItems, sortConfig, pastSearchTerm, approversMap]);

    // --- STEP 2: Calculate paginated data for the "Past" tab ---
    const paginatedPastItems = useMemo(() => {
        // Only paginate if the active tab is 'past'
        if (activeTab === 'past') {
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            return sortedItems.slice(startIndex, endIndex);
        }
        // For the 'pending' tab, return all sorted items without pagination
        return sortedItems;
    }, [sortedItems, currentPage, itemsPerPage, activeTab]);

    const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
        setCurrentPage(1); // Reset pagination when switching tabs
    };

    const handleSearchChange = (event) => {
        setPastSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1); // Reset to first page on sort
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-center text-red-500 p-4">{error}</div>;

    return (
        <>
            {isModalOpen && (
                <ApprovalDetailModal
                    item={selectedItem}
                    onClose={() => setIsModalOpen(false)}
                    appId={appId}
                />
            )}
            {isRejectModalOpen && (
                 <RejectModal
                    item={selectedItem}
                    onClose={() => setIsRejectModalOpen(false)}
                    onReject={handleAction}
                 />
            )}
            <div>
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">Approval Center</h2>
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button onClick={() => handleTabClick('pending')} className={`${activeTab === 'pending' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                            Pending ({approvalItems.length})
                        </button>
                        <button onClick={() => handleTabClick('past')} className={`${activeTab === 'past' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                            Past ({pastItems.length})
                        </button>
                    </nav>
                </div>

                {activeTab === 'past' && (
                    <div className="mb-4">
                        <input
                            type="text"
                            value={pastSearchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search past approvals by ID, vendor, or requester..."
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                        />
                    </div>
                )}

                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        {sortedItems.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th onClick={() => requestSort('type')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Type</th>
                                        <th onClick={() => requestSort('id')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">ID</th>
                                        <th onClick={() => requestSort(activeTab === 'pending' ? 'dueDate' : 'approvalDate')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">
                                            {activeTab === 'pending' ? 'Due Date' : 'Approval Date'}
                                        </th>
                                        <th onClick={() => requestSort('requester')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Requester</th>
					<th onClick={() => requestSort('approver')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Approver</th>                                        
					<th onClick={() => requestSort('vendor')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                        <th onClick={() => requestSort('amount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                        {activeTab === 'past' && <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {/* --- STEP 3: Render the paginated data --- */}
                                    {paginatedPastItems.map(item => {
                                        // --- FIX #6: Look up the approver ID ---
                                        const approverId = item.assignedApproverId || item.approvedById;
                                        return (
                                        <tr key={item.id}>
                                            <td className="px-6 py-4"><span className={`px-2 py-1 text-xs rounded-full ${item.type === 'Purchase' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{item.type}</span></td>
                                            <td className="px-6 py-4 text-sm font-mono">{item.invoiceNumber || item.requestNumber}</td>
                                            <td className="px-6 py-4 text-sm">{(activeTab === 'pending' ? item.dueDate : item.approvalDate)?.toDate().toLocaleDateString()}</td>
                                            <td className="px-6 py-4 text-sm">{item.requesterName || item.submittedBy}</td>
					    <td className="px-6 py-4 text-sm">{approversMap.get(approverId) || 'N/A'}</td>                                            
					    <td className="px-6 py-4 text-sm">{item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor)}</td>
                                            <td className="px-6 py-4 text-sm">{item.currency} {item.approxValue || item.amount}</td>
                                            {activeTab === 'past' && <td className="px-6 py-4 text-sm"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Approved' || item.status === 'Approved for Payment' || item.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{item.status}</span></td>}
                                            <td className="px-6 py-4 text-sm">
                                                <div className="flex items-center space-x-3">
                                                    <button onClick={() => { setSelectedItem(item); setIsModalOpen(true); }} className="p-2 text-gray-500 hover:text-[#20BEC6]" title="View Details"><EyeIcon /></button>
                                                    {activeTab === 'pending' && (
                                                        <>
                                                            <button onClick={() => handleAction(item, 'Approved')} className="p-2 text-green-500 hover:text-green-700" title="Approve"><CheckCircleIcon /></button>
                                                            <button onClick={() => handleQuickReject(item)} className="p-2 text-red-500 hover:text-red-700" title="Reject"><XCircleIcon /></button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
				        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-12 px-6">
                                <h3 className="mt-2 text-sm font-medium text-[#213368]">All Clear!</h3>
                                <p className="mt-1 text-sm text-gray-500">There are no items in this view.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- STEP 4: Add pagination controls, visible only for the "Past" tab --- */}
                {activeTab === 'past' && totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center space-x-2 text-sm">
                            <span>Items per page:</span>
                            <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                Previous
                            </button>
                            <span className="text-sm">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

function RejectModal({ item, onClose, onReject }) {
    const [reason, setReason] = useState('');

    const handleSubmit = () => {
        if (!reason.trim()) {
            alert("A reason is required for rejection.");
            return;
        }
        onReject(item, 'Rejected', reason);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-[#213368] mb-4">Reject Item</h2>
                <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejecting {item.invoiceNumber || item.requestNumber}.</p>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Rejection comments..."
                ></textarea>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSubmit} className="px-6 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600">Confirm Rejection</button>
                </div>
            </div>
        </div>
    );
}